import { Fragment, ReactNode, useMemo } from 'react';
import { OptimusTextKey, useLocalization } from '@optimus-web/localization';
import { UnicodeEntity } from '@mews/enums';
import { MultiselectDisplayValues } from '../types';

/**
 * Creates an accessible label for screen readers that combines the current selection
 * with field label and helper text. This provides context for screen reader users
 * when the visual display shows selected values separately from the input field.
 */

interface UseScreenReaderLabelProps {
    label?: ReactNode;
    helper?: ReactNode;
    error?: ReactNode;
    multiselect?: boolean;
    displayValues: MultiselectDisplayValues;
    displayedInputValue?: ReactNode;
}

const renderLabelPart = (part: ReactNode) =>
    part ? (
        /**
         * There's a known issue with React and DOM manipulation coming from browser translators.
         * https://github.com/facebook/react/issues/11538
         * Deep dive https://martijnhols.nl/blog/everything-about-google-translate-crashing-react
         * That's why we need to wrap the text in a span.
         * */
        <span>
            {UnicodeEntity.Comma}
            {UnicodeEntity.Space}
            {part}
        </span>
    ) : null;

export const useScreenReaderLabel = ({
    label,
    helper,
    error,
    multiselect = false,
    displayValues,
    displayedInputValue,
}: UseScreenReaderLabelProps) => {
    const { t } = useLocalization();

    return useMemo(() => {
        const getSelectionText = () => {
            if (multiselect) {
                if (displayValues.length === 0) {
                    return t(OptimusTextKey.NoItems);
                }

                return (
                    <>
                        {t(OptimusTextKey.ItemsSelected, { count: displayValues.length })}
                        {UnicodeEntity.Colon}
                        {UnicodeEntity.Space}
                        {displayValues.map((v, index) => (
                            <Fragment key={v.key}>
                                {index > 0 && ', '}
                                {v.val}
                            </Fragment>
                        ))}
                    </>
                );
            }

            return displayedInputValue ?? t(OptimusTextKey.NotSelected);
        };

        return (
            <>
                {/**
                 * There's a known issue with React and DOM manipulation coming from browser translators.
                 * https://github.com/facebook/react/issues/11538
                 * Deep dive https://martijnhols.nl/blog/everything-about-google-translate-crashing-react
                 * That's why we need to wrap the selection text in a span.
                 * */}
                <span>{getSelectionText()}</span>
                {renderLabelPart(label)}
                {renderLabelPart(helper)}
                {renderLabelPart(error)}
            </>
        );
    }, [label, helper, error, multiselect, displayValues, displayedInputValue, t]);
};
